import { ChangeEvent, useReducer, useState } from "react";
import { FileEditAltIcon, TextInput, ToggleInput } from "@datagame/ui";
import { useTranslation } from "react-i18next";
import { omit } from "lodash";

import {
  LeaderboardConfiguration,
  LeaderboardConfigurationUpdateInput,
  LibraryImage,
  LibraryImageKind,
} from "../../../../../../../gql/graphql";
import { ColorInput } from "../../../../../../shared/ui/ColorInput";
import { ImageInput, Section } from "../../Common";
import { TooltipWithIcon } from "../../../../../../shared/ui/TooltipWithIcon";

import styles from "./LeaderboardConfigSection.module.scss";

export interface LeaderboardConfigSectionProps {
  leaderboardConfiguration: LeaderboardConfiguration;
  onUpdate: (data: LeaderboardConfigurationUpdateInput) => void;
}

enum ActionTypes {
  updatenicknameQuestion = "updatenicknameQuestion",
  updatenicknameQuestionColor = "updatenicknameQuestionColor",
  updateHeader = "updateHeader",
  updateHeaderColor = "updateHeaderColor",
  updateSubheader = "updateSubheader",
  updateSubheaderColor = "updateSubheaderColor",
  updateNicknameLabel = "updateNicknameLabel",
  updateTableNicknameLabel = "updateTableNicknameLabel",
  updateTableScoreLabel = "updateTableScoreLabel",
  updateTableTimeLabel = "updateTableTimeLabel",
  updateShowTimer = "updateShowTimer",
  updateNumberOfPlayers = "updateNumberOfPlayers",
  updateBackground = "updateBackground",
}

type Actions =
  | { type: ActionTypes.updatenicknameQuestion; value: string }
  | { type: ActionTypes.updatenicknameQuestionColor; value: string }
  | { type: ActionTypes.updateHeader; value: string }
  | { type: ActionTypes.updateHeaderColor; value: string }
  | { type: ActionTypes.updateSubheader; value: string }
  | { type: ActionTypes.updateSubheaderColor; value: string }
  | { type: ActionTypes.updateNicknameLabel; value: string }
  | { type: ActionTypes.updateTableNicknameLabel; value: string }
  | { type: ActionTypes.updateTableScoreLabel; value: string }
  | { type: ActionTypes.updateTableTimeLabel; value: string }
  | { type: ActionTypes.updateShowTimer; value: boolean }
  | { type: ActionTypes.updateNumberOfPlayers; value: number }
  | { type: ActionTypes.updateBackground; imageId?: string | null; color?: string };

type State = {
  nicknameQuestion: string;
  nicknameQuestionColor: string;
  header: string;
  headerColor: string;
  subheader: string;
  subheaderColor: string;
  nicknameLabel: string;
  tableNicknameLabel: string;
  tableScoreLabel: string;
  tableTimeLabel: string;
  showTimer: boolean;
  numberOfPlayers: number;
  backgroundColor: string;
  backgroundImageId?: string | null;
};

const reducer = (state: State, action: Actions) => {
  switch (action.type) {
    case ActionTypes.updatenicknameQuestion: {
      return {
        ...state,
        nicknameQuestion: action.value,
      };
    }
    case ActionTypes.updatenicknameQuestionColor: {
      return {
        ...state,
        nicknameQuestionColor: action.value,
      };
    }
    case ActionTypes.updateHeader: {
      return {
        ...state,
        header: action.value,
      };
    }
    case ActionTypes.updateHeaderColor: {
      return {
        ...state,
        headerColor: action.value,
      };
    }
    case ActionTypes.updateSubheader: {
      return {
        ...state,
        subheader: action.value,
      };
    }
    case ActionTypes.updateSubheaderColor: {
      return {
        ...state,
        subheaderColor: action.value,
      };
    }
    case ActionTypes.updateNicknameLabel: {
      return {
        ...state,
        nicknameLabel: action.value,
      };
    }
    case ActionTypes.updateTableNicknameLabel: {
      return {
        ...state,
        tableNicknameLabel: action.value,
      };
    }
    case ActionTypes.updateTableScoreLabel: {
      return {
        ...state,
        tableScoreLabel: action.value,
      };
    }
    case ActionTypes.updateTableTimeLabel: {
      return {
        ...state,
        tableTimeLabel: action.value,
      };
    }
    case ActionTypes.updateShowTimer: {
      return {
        ...state,
        showTimer: action.value,
      };
    }
    case ActionTypes.updateNumberOfPlayers: {
      return {
        ...state,
        numberOfPlayers: action.value,
      };
    }
    case ActionTypes.updateBackground: {
      const { imageId, color } = action;

      if (imageId !== undefined) {
        return {
          ...state,
          backgroundImageId: imageId,
        };
      }

      if (color && color !== state.backgroundColor) {
        return {
          ...state,
          backgroundColor: color,
          backgroundImageId: null,
        };
      }

      return state;
    }
  }
};

export const LeaderboardConfigSection = ({ leaderboardConfiguration, onUpdate }: LeaderboardConfigSectionProps) => {
  const { t } = useTranslation();

  const [state, dispatch] = useReducer(reducer, {
    ...leaderboardConfiguration,
    backgroundImage: undefined,
    backgroundImageId: leaderboardConfiguration?.backgroundImage?.id,
  } as State);

  const [backgroundImage, setBackgroundImage] = useState<LibraryImage | null>(
    leaderboardConfiguration?.backgroundImage ?? null,
  );
  const [isBackgroundImageEnabled, setIsBackgroundImageEnabled] = useState<boolean>(Boolean(state?.backgroundImageId));

  const handleChangeShowTimer = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: ActionTypes.updateShowTimer, value: event.target.checked });
    onUpdate({ showTimer: event.target.checked });
  };

  const handleUpdate = (overwrittenState: Partial<State> = {}) => {
    onUpdate(omit({ ...state, ...(overwrittenState ?? {}) }, ["__typename", "id"]));
  };

  const handleUploadBackgroundImage = (image: LibraryImage) => {
    setBackgroundImage(image);
    dispatch({ type: ActionTypes.updateBackground, imageId: image.id });
    handleUpdate({ backgroundImageId: image.id });
  };

  const handleBackgroundImageRemove = () => {
    setBackgroundImage(null);
    dispatch({ type: ActionTypes.updateBackground, imageId: null });
    handleUpdate({ backgroundImageId: null });
  };

  const handleBackgroundImageToggle = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
    setIsBackgroundImageEnabled(checked);
    if (!checked) {
      handleBackgroundImageRemove();
    }
  };

  const renderEditSection = () => {
    if (isBackgroundImageEnabled) {
      return (
        <ImageInput
          image={backgroundImage}
          onUpload={handleUploadBackgroundImage}
          kind={LibraryImageKind.Background}
          showRemoveButton={true}
          onRemove={handleBackgroundImageRemove}
        />
      );
    }
    return (
      <ColorInput
        value={state.backgroundColor}
        onChange={(color) => dispatch({ type: ActionTypes.updateBackground, color })}
        onBlur={() => handleUpdate()}
      />
    );
  };

  const backgroundSection = (
    <Section className={styles.backgroundSection} title={t("leaderboard:background")}>
      <ToggleInput
        name="useBackgroundImage"
        className={styles.backgroundImageToggle}
        value={isBackgroundImageEnabled}
        onChange={handleBackgroundImageToggle}
        label={t("leaderboard:useBackgroundImage") ?? ""}
        tooltip={<TooltipWithIcon content={t("leaderboard:useBackgroundImageTooltip")} />}
      />
      {renderEditSection()}
    </Section>
  );

  return (
    <div className={styles.formWrapper}>
      <div className={styles.fieldWrapper}>
        <Section
          className={styles.section}
          title={t("leaderboard:nicknameQuestion")}
          tooltip={<TooltipWithIcon content={t("leaderboard:nicknameQuestionTooltip")} />}
        >
          <TextInput
            size="small"
            name="nicknameQuestion"
            icon={<FileEditAltIcon />}
            placeholder={t("leaderboard:nicknameQuestionPlaceholder") ?? ""}
            value={state?.nicknameQuestion}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              dispatch({ type: ActionTypes.updatenicknameQuestion, value: event.target.value })
            }
            onBlur={() => handleUpdate()}
          />
        </Section>

        <Section
          className={styles.section}
          title={t("leaderboard:nicknameQuestionColor")}
          tooltip={<TooltipWithIcon content={t("leaderboard:nicknameQuestionColorTooltip")} />}
        >
          <ColorInput
            value={state?.nicknameQuestionColor}
            onChange={(color) => dispatch({ type: ActionTypes.updatenicknameQuestionColor, value: color })}
            onBlur={() => handleUpdate()}
          />
        </Section>
      </div>

      <div className={styles.fieldWrapper}>
        <Section
          className={styles.section}
          title={t("leaderboard:header")}
          tooltip={<TooltipWithIcon content={t("leaderboard:headerTooltip")} />}
        >
          <TextInput
            size="small"
            name="header"
            icon={<FileEditAltIcon />}
            placeholder={t("leaderboard:headerPlaceholder") ?? ""}
            value={state?.header}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              dispatch({ type: ActionTypes.updateHeader, value: event.target.value })
            }
            onBlur={() => handleUpdate()}
          />
        </Section>

        <Section
          className={styles.section}
          title={t("leaderboard:headerColor")}
          tooltip={<TooltipWithIcon content={t("leaderboard:headerColorTooltip")} />}
        >
          <ColorInput
            value={state?.headerColor}
            onChange={(color) => dispatch({ type: ActionTypes.updateHeaderColor, value: color })}
            onBlur={() => handleUpdate()}
          />
        </Section>
      </div>
      <div className={styles.fieldWrapper}>
        <Section
          className={styles.section}
          title={t("leaderboard:subheader")}
          tooltip={<TooltipWithIcon content={t("leaderboard:subheaderTooltip")} />}
        >
          <TextInput
            size="small"
            name="subheader"
            icon={<FileEditAltIcon />}
            placeholder={t("leaderboard:subheaderPlaceholder") ?? ""}
            value={state?.subheader}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              dispatch({ type: ActionTypes.updateSubheader, value: event.target.value })
            }
            onBlur={() => handleUpdate()}
          />
        </Section>

        <Section
          className={styles.section}
          title={t("leaderboard:subheaderColor")}
          tooltip={<TooltipWithIcon content={t("leaderboard:subheaderColorTooltip")} />}
        >
          <ColorInput
            value={state?.subheaderColor}
            onChange={(color) => dispatch({ type: ActionTypes.updateSubheaderColor, value: color })}
            onBlur={() => handleUpdate()}
          />
        </Section>
      </div>
      <div className={styles.fieldWrapper}>
        <Section
          className={styles.section}
          title={t("leaderboard:nicknameLabel")}
          tooltip={<TooltipWithIcon content={t("leaderboard:nicknameLabelTooltip")} />}
        >
          <TextInput
            size="small"
            name="nicknameLabel"
            icon={<FileEditAltIcon />}
            placeholder={t("leaderboard:nicknameLabelPlaceholder") ?? ""}
            value={state?.nicknameLabel}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              dispatch({ type: ActionTypes.updateNicknameLabel, value: event.target.value })
            }
            onBlur={() => handleUpdate()}
          />
        </Section>

        <Section
          className={styles.section}
          title={t("leaderboard:tableNicknameLabel")}
          tooltip={<TooltipWithIcon content={t("leaderboard:tableNicknameLabelTooltip")} />}
        >
          <TextInput
            size="small"
            name="tableNicknameLabel"
            icon={<FileEditAltIcon />}
            placeholder={t("leaderboard:tableNicknameLabelPlaceholder") ?? ""}
            value={state?.tableNicknameLabel}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              dispatch({ type: ActionTypes.updateTableNicknameLabel, value: event.target.value })
            }
            onBlur={() => handleUpdate()}
          />
        </Section>
      </div>
      <div className={styles.fieldWrapper}>
        <Section
          className={styles.section}
          title={t("leaderboard:numberOfPlayers")}
          tooltip={<TooltipWithIcon content={t("leaderboard:numberOfPlayersTooltip")} />}
        >
          <TextInput
            min={1}
            type="number"
            size="small"
            name="numberOfPlayers"
            icon={<FileEditAltIcon />}
            value={state?.numberOfPlayers}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              dispatch({ type: ActionTypes.updateNumberOfPlayers, value: event.target.valueAsNumber })
            }
            onBlur={() => handleUpdate()}
          />
        </Section>

        <Section
          className={styles.section}
          title={t("leaderboard:tableScoreLabel")}
          tooltip={<TooltipWithIcon content={t("leaderboard:tableScoreLabelTooltip")} />}
        >
          <TextInput
            size="small"
            name="tableScoreLabel"
            icon={<FileEditAltIcon />}
            placeholder={t("leaderboard:tableScoreLabelPlaceholder") ?? ""}
            value={state?.tableScoreLabel}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              dispatch({ type: ActionTypes.updateTableScoreLabel, value: event.target.value })
            }
            onBlur={() => handleUpdate()}
          />
        </Section>
      </div>

      <div className={styles.fieldWrapper}>
        <Section className={styles.section} title="">
          <ToggleInput
            name="showTimer"
            value={state?.showTimer}
            onChange={handleChangeShowTimer}
            label={t("leaderboard:showTimer") ?? ""}
            tooltip={<TooltipWithIcon content={t("leaderboard:showTimerTooltip")} />}
          />
        </Section>
        {state?.showTimer && (
          <Section
            className={styles.section}
            title={t("leaderboard:tableTimeLabel")}
            tooltip={<TooltipWithIcon content={t("leaderboard:tableTimeLabelTooltip")} />}
          >
            <TextInput
              size="small"
              name="tableTimeLabel"
              icon={<FileEditAltIcon />}
              placeholder={t("leaderboard:tableTimeLabelPlaceholder") ?? ""}
              value={state?.tableTimeLabel}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                dispatch({ type: ActionTypes.updateTableTimeLabel, value: event.target.value })
              }
              onBlur={() => handleUpdate()}
            />
          </Section>
        )}
      </div>
      {backgroundSection}
    </div>
  );
};
